import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FoodItem from "./FoodItem";
import Skeleton from "./Skeleton";
import AutoScrollCategories from "./AutoScrollCategories";
import LocationButton from "../Header/LocationButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts, clearProducts } from "../../redux/menu/ProductSlice";
import { fetchCategories } from "../../redux/menu/CategoriesSlice";
import { fetchSubCategoriesFromCategories, clearSubCategories } from "../../redux/menu/SubCategoriesSlice";
import { useNavigate } from "react-router-dom";
import { BsCart2 } from "react-icons/bs";
import { FaTimes, FaWhatsapp, FaBars, FaSearch, FaChevronDown } from "react-icons/fa";
import useTheme from "../../hooks/useTheme";

// Add mobile-specific styles and animations
const mobileStyles = `
  @media (max-width: 640px) {
    .category-division {
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding-left: 16px !important;
      padding-right: 16px !important;
      will-change: transform;
      transform: translateZ(0);
      backface-visibility: hidden;
      perspective: 1000px;
    }
    .category-division::-webkit-scrollbar {
      display: none;
    }
    .sticky-categories {
      top: 64px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      z-index: 50 !important;
    }
  }
  @media (min-width: 641px) and (max-width: 768px) {
    .sticky-categories {
      top: 64px !important;
    }
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    .sticky-categories {
      top: 88px !important;
    }
  }
  @media (min-width: 1025px) {
    .sticky-categories {
      top: 120px !important;
    }
  }

  /* Animation Keyframes */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes bounceSubtle {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-5px);
    }
    60% {
      transform: translateY(-3px);
    }
  }

  @keyframes pulseSubtle {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
  }

  /* Animation Classes */
  .animate-fade-in-up {
    animation: fadeInUp 0.4s ease-out forwards; /* Reduced from 0.6s to 0.4s */
  }

  .animate-fade-in {
    animation: fadeIn 0.4s ease-out forwards;
  }

  .animate-bounce-subtle {
    animation: bounceSubtle 2s infinite;
  }

  .animate-pulse-subtle {
    animation: pulseSubtle 2s infinite;
  }

  /* Staggered animation delays */
  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }
  .animate-fade-in-up:nth-child(7) { animation-delay: 0.7s; }
  .animate-fade-in-up:nth-child(8) { animation-delay: 0.8s; }

  /* Scroll-based loading indicator */
  .scroll-loading-indicator {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 12px 16px;
    border-radius: 25px;
    font-size: 14px;
    font-weight: 500;
    z-index: 1000;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    transform: translateY(100px);
    opacity: 0;
    transition: all 0.3s ease-out;
  }

  .scroll-loading-indicator.show {
    transform: translateY(0);
    opacity: 1;
  }

  .scroll-loading-indicator .loading-dots {
    display: inline-block;
    margin-left: 8px;
  }

  .scroll-loading-indicator .loading-dots span {
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: #fff;
    margin: 0 1px;
    animation: loadingDots 1.4s infinite ease-in-out both;
  }

  .scroll-loading-indicator .loading-dots span:nth-child(1) { animation-delay: -0.32s; }
  .scroll-loading-indicator .loading-dots span:nth-child(2) { animation-delay: -0.16s; }

  @keyframes loadingDots {
    0%, 80%, 100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
`;

const Foods = ({
  showCartButton = true,
  showAddToCart = true,
  highlightInfoIcon = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const menuReduxValue = useSelector((state) => state.menu);
  const { categories, status: categoriesStatus, pagination: categoriesPagination, isLoadingMore: isLoadingMoreCategories } = useSelector(
    (state) => state.category
  );
  
  // Search query state must be declared before useMemo that uses it
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  
  // Normalize categories to an array (supports { list: [...] } or array) with search filtering
  const categoriesArray = useMemo(() => {
    let allCategories = [];
    if (Array.isArray(categories)) allCategories = categories;
    else if (categories && Array.isArray(categories.list)) allCategories = categories.list;
    
    // Filter by search query
    if (categorySearchQuery.trim()) {
      const query = categorySearchQuery.toLowerCase().trim();
      return allCategories.filter((cat) => 
        cat.name?.toLowerCase().includes(query)
      );
    }
    
    return allCategories;
  }, [categories, categorySearchQuery]);

  // Use all categories for infinite scroll (removed 20 category limit)
  const limitedCategories = useMemo(() => categoriesArray, [categoriesArray]);
  
  // Category modal pagination state
  const [categoryPage, setCategoryPage] = useState(1);

  const {
    products,
    status: productsStatus,
    isSearchMode,
    isOffersMode,
    pagination,
    isLoadingMore,
    selectedCategoryId,
    searchQuery,
  } = useSelector((state) => state.product);
  const { profile } = useSelector((state) => state.profile);
  const { banners, status: bannerStatus } = useSelector((state) => state.banner);
  const { subCategories, status: subCategoriesStatus } = useSelector((state) => state.subCategory);
  const cartItems = useSelector((state) => state.cart.cart);

  // Normalize products to an array (supports { list: [...] } or array)
  const productsArray = useMemo(() => {
    if (Array.isArray(products)) return products;
    if (products && Array.isArray(products.list)) return products.list;
    if (products && products.data && Array.isArray(products.data.list)) return products.data.list;
    return [];
  }, [products]);

  // Sticky
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef(null);
  
  // Check if banner exists to adjust spacing
  const bannerList = React.useMemo(() => banners?.banner || banners || [], [banners]);
  const hasBanner = (bannerStatus === 'succeeded' || bannerStatus === 'failed') && bannerList && bannerList.length > 0;

  const [menuTab, setMenuTab] = useState(menuReduxValue?.selectedCategory);
  const [currentCategoryId, setCurrentCategoryId] = useState("");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [subCategoriesExpanded, setSubCategoriesExpanded] = useState(false);
  const [subCategoriesOverflow, setSubCategoriesOverflow] = useState(false);
  const [firstRowHeight, setFirstRowHeight] = useState(60);
  const [currentPage, setCurrentPage] = useState(1);
  const productsGridRef = useRef(null);
  const subCategoriesContainerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const isCategoryLoadingRef = useRef(false); // Prevent duplicate category/subcategory calls
  const isHandledByMenuTabsRef = useRef(false); // Track if handleMenuTabs is handling the change
  const lastProcessedCategoryIdRef = useRef(null); // Track last processed category ID to prevent duplicates
  const categorySetByMenuTabsRef = useRef(null); // Track category name set by handleMenuTabs
  const [isNearBottom, setIsNearBottom] = useState(false);

  // Load initial categories when modal opens
  useEffect(() => {
    if (showCategoryModal && categoriesArray.length === 0) {
      dispatch(fetchCategories({ page: 1, limit: 20 }));
    }
  }, [showCategoryModal, categoriesArray.length, dispatch]);
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Update whatsapp message when search query changes
  useEffect(() => {
    if (searchQuery) {
      setWhatsappMessage(`Hi, I'm looking for: ${searchQuery}`);
    }
  }, [searchQuery]);

  // Set default message for offers mode
  useEffect(() => {
    if (isOffersMode) {
      setWhatsappMessage("Hi, I'm looking for: offer");
    }
  }, [isOffersMode]);

  // Clear search when modal closes
  useEffect(() => {
    if (!showCategoryModal) {
      setCategorySearchQuery("");
    }
  }, [showCategoryModal]);

  // Reset expanded state when category changes
  useEffect(() => {
    setSubCategoriesExpanded(false);
  }, [currentCategoryId]);

  // Check if subcategories overflow (more than one row) and calculate first row height
  useEffect(() => {
    const checkOverflow = () => {
      if (subCategoriesContainerRef.current) {
        const container = subCategoriesContainerRef.current;
        const children = Array.from(container.children);
        
        if (children.length === 0) {
          setSubCategoriesOverflow(false);
          setFirstRowHeight(60);
          return;
        }

        // Get the height of the first child (button) to estimate row height
        const firstChild = children[0];
        if (!firstChild) {
          setSubCategoriesOverflow(false);
          setFirstRowHeight(60);
          return;
        }

        // Temporarily remove maxHeight to measure full container height
        const originalMaxHeight = container.style.maxHeight;
        const originalOverflow = container.style.overflow;
        container.style.maxHeight = 'none';
        container.style.overflow = 'visible';
        
        // Force reflow to get accurate measurement
        void container.offsetHeight;
        
        // Calculate the exact height of the first row
        // Get the top position of the first item relative to container
        const firstItemTop = firstChild.offsetTop;
        let firstRowBottom = firstItemTop + firstChild.offsetHeight;
        
        // Find all items that are on the same row (same top position, allowing 1px tolerance for rounding)
        children.forEach((child) => {
          const childTop = child.offsetTop;
          if (Math.abs(childTop - firstItemTop) <= 1) {
            // This item is on the first row
            const childBottom = childTop + child.offsetHeight;
            if (childBottom > firstRowBottom) {
              firstRowBottom = childBottom;
            }
          }
        });
        
        // First row height is from top of first item to bottom of tallest item in first row
        // Add buffer to ensure the full row is visible (more buffer on desktop for better visibility)
        const containerRect = container.getBoundingClientRect();
        const isDesktop = containerRect.width >= 768;
        const buffer = isDesktop ? 6 : 2; // More buffer on desktop to prevent any cut-off
        const calculatedFirstRowHeight = firstRowBottom - firstItemTop + buffer;
        
        const containerHeight = container.scrollHeight;
        
        // Restore original styles
        container.style.maxHeight = originalMaxHeight;
        container.style.overflow = originalOverflow;
        
        // If container height is more than first row height, it means multiple rows
        // Use a small buffer to account for rounding
        const hasOverflow = containerHeight > calculatedFirstRowHeight + 2;
        
        setSubCategoriesOverflow(hasOverflow);
        setFirstRowHeight(calculatedFirstRowHeight);
      }
    };

    // Check overflow after a short delay to ensure DOM is updated
    const timeoutId = setTimeout(checkOverflow, 200);
    
    // Also check on window resize
    window.addEventListener('resize', checkOverflow);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [subCategories, currentCategoryId, selectedSubcategoryId, subCategoriesExpanded]);

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showCategoryModal) {
          setShowCategoryModal(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showCategoryModal]);

  const loadProducts = useCallback(
    (categoryId, page = 1, subCategoryId = null) => {
      if (categoryId && !isLoadingRef.current) {
        isLoadingRef.current = true;
        // Use requestIdleCallback for better performance if available
        const loadProductsTask = () => {
          const params = { categoryID: categoryId, page };
          // Only add subCategoryId if it's not null (i.e., a subcategory is selected)
          if (subCategoryId) {
            params.subCategoryId = subCategoryId;
          }
          dispatch(fetchProducts(params)).finally(() => {
            isLoadingRef.current = false;
          });
        };
        
        if (window.requestIdleCallback) {
          window.requestIdleCallback(loadProductsTask, { timeout: 1000 });
        } else {
          setTimeout(loadProductsTask, 0);
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (limitedCategories && limitedCategories.length > 0) {
      if (!menuTab && limitedCategories[0] && !isCategoryLoadingRef.current) {
        isCategoryLoadingRef.current = true;
        setMenuTab(limitedCategories[0].name);
        setCurrentCategoryId(limitedCategories[0].id);
        setCurrentPage(1);
        setSelectedSubcategoryId(null); // Reset subcategory when changing main category
        
        // First fetch subcategories, then fetch products
        if (limitedCategories[0].id) {
          dispatch(fetchSubCategoriesFromCategories(limitedCategories[0].id)).then(() => {
            // After subcategories are fetched, load products
            loadProducts(limitedCategories[0].id, 1, null);
            isCategoryLoadingRef.current = false;
          }).catch(() => {
            isCategoryLoadingRef.current = false;
          });
        } else {
          loadProducts(limitedCategories[0].id, 1, null);
          isCategoryLoadingRef.current = false;
        }
      }
    }
  }, [limitedCategories, menuTab, dispatch, loadProducts]);

  // This useEffect handles category changes from other sources (like Redux selectedCategoryId)
  // It should NOT run when handleMenuTabs is handling the change
  useEffect(() => {
    // IMPORTANT: Check flags FIRST before any other logic to prevent duplicate calls
    // Skip if:
    // 1. handleMenuTabs is handling this change
    // 2. We're already loading
    // 3. This menuTab was just set by handleMenuTabs (most important check)
    if (isHandledByMenuTabsRef.current || 
        isCategoryLoadingRef.current || 
        (menuTab && menuTab === categorySetByMenuTabsRef.current)) {
      return;
    }
    
    // Only handle category changes from external sources (not from handleMenuTabs)
    if (menuTab && limitedCategories) {
      const selectedCategory = limitedCategories.find((cat) => cat.name === menuTab);
      
      // Only proceed if:
      // 1. Category exists
      // 2. Category ID is different from current (actual change)
      // 3. Category ID hasn't been processed recently
      // 4. This category was NOT set by handleMenuTabs (double check)
      if (selectedCategory && 
          selectedCategory.id !== currentCategoryId && 
          selectedCategory.id !== lastProcessedCategoryIdRef.current &&
          selectedCategory.name !== categorySetByMenuTabsRef.current) {
        // Mark this category as being processed
        lastProcessedCategoryIdRef.current = selectedCategory.id;
        
        // Clear old subcategories before switching categories
        dispatch(clearSubCategories());
        
        isCategoryLoadingRef.current = true;
        setIsTransitioning(true);
        setCurrentCategoryId(selectedCategory.id);
        setCurrentPage(1);
        setSelectedSubcategoryId(null); // Reset subcategory when category changes
        
        // Capture categoryId in a const
        const categoryIdForAPI = selectedCategory.id;
        
        // First fetch subcategories, then fetch products
        if (selectedCategory.id) {
          dispatch(fetchSubCategoriesFromCategories(categoryIdForAPI)).then(() => {
            // After subcategories are fetched, load products
            loadProducts(categoryIdForAPI, 1, null);
            isCategoryLoadingRef.current = false;
          }).catch(() => {
            isCategoryLoadingRef.current = false;
          });
        } else {
          // If no category ID, just load products
          loadProducts(selectedCategory.id, 1, null);
          isCategoryLoadingRef.current = false;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTab, limitedCategories, loadProducts, dispatch]); // currentCategoryId intentionally excluded to prevent stale value issues

  // Reset transition when products are loaded
  useEffect(() => {
    if (productsStatus === "succeeded" && products.length > 0) {
      setIsTransitioning(false);
    }
  }, [productsStatus, products.length]);

  // Sync with Redux selectedCategoryId (when category is selected from search results)
  useEffect(() => {
    if (selectedCategoryId && categories) {
      const category = categories.find((cat) => cat.id === selectedCategoryId);
      if (category && category.name !== menuTab) {
        setMenuTab(category.name);
        setCurrentCategoryId(selectedCategoryId);
      }
    }
  }, [selectedCategoryId, categories, menuTab]);

  // Cart persistence is handled by localStorage, no need to reset on mount

  const handleMenuTabs = useCallback((type, index, categoryId = null) => {
    // Don't re-select if the same category is already selected
    if (menuTab === type) {
      return;
    }
    
    // Use the categoryId passed directly, or find it by name as fallback
    let selectedCategoryId = categoryId;
    if (!selectedCategoryId) {
      const selectedCategory = limitedCategories?.find((cat) => cat.name === type);
      selectedCategoryId = selectedCategory?.id;
    }
    
    if (selectedCategoryId) {
      // Skip if this category is already being processed
      if (selectedCategoryId === lastProcessedCategoryIdRef.current && isCategoryLoadingRef.current) {
        return;
      }
      
      // IMPORTANT: Set BOTH flags FIRST before any state updates to prevent useEffect from running
      // Also mark the category as processed immediately
      isHandledByMenuTabsRef.current = true;
      isCategoryLoadingRef.current = true;
      lastProcessedCategoryIdRef.current = selectedCategoryId; // Mark this category as being processed
      categorySetByMenuTabsRef.current = type; // Track which category name was set by handleMenuTabs
      
      // Clear old subcategories before switching categories
      dispatch(clearSubCategories());
      
      // Capture categoryId in a const BEFORE state updates to ensure we use the correct one
      const categoryIdForAPI = selectedCategoryId;
      
      setMenuTab(type);
      setCurrentPage(1);
      // Set category ID immediately to avoid using stale ID
      setCurrentCategoryId(selectedCategoryId);
      setSelectedSubcategoryId(null); // Reset subcategory when category changes
      setIsTransitioning(true);
      
      // Fetch subcategories and products with the correct category ID
      // Use the captured categoryIdForAPI to ensure we use the correct ID even if state changes
      dispatch(fetchSubCategoriesFromCategories(categoryIdForAPI)).then(() => {
        // After subcategories are fetched, load products
        loadProducts(categoryIdForAPI, 1, null);
        isCategoryLoadingRef.current = false;
        
        // Reset the flag after a delay to allow state updates to settle
        setTimeout(() => {
          isHandledByMenuTabsRef.current = false;
          categorySetByMenuTabsRef.current = null; // Clear after delay
        }, 1000); // Increased delay to ensure useEffect doesn't run
      }).catch(() => {
        isCategoryLoadingRef.current = false;
        setTimeout(() => {
          isHandledByMenuTabsRef.current = false;
          categorySetByMenuTabsRef.current = null; // Clear after delay
        }, 1000);
      });
    }
  }, [menuTab, limitedCategories, loadProducts, dispatch]);

  // Load more categories for modal (API pagination like products)
  const loadMoreCategories = useCallback(() => { 
    if (isLoadingMoreCategories || !categoriesPagination.hasMore) {
      return;
    }
    
    const nextPage = categoryPage + 1;
    setCategoryPage(nextPage);
    dispatch(fetchCategories({ page: nextPage, limit: 20 }));
  }, [categoryPage, categoriesPagination.hasMore, isLoadingMoreCategories, dispatch]);

  // Optimized infinite scroll handler with performance improvements
  const handleScroll = useCallback(() => {
    if (!productsGridRef.current || isSearchMode || isOffersMode) {
      return;
    }
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    
    
    // Check if near bottom (within 200px)
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    const nearBottom = distanceFromBottom < 200;
    
    // Only update nearBottom state if it changed
    if (nearBottom !== isNearBottom) {
      setIsNearBottom(nearBottom);
    }
    
    // Trigger load more when user scrolls to 80% of the page OR gets within 200px of bottom
    const shouldLoadMore = (scrollTop + clientHeight >= scrollHeight * 0.8) || nearBottom;
    
    if (shouldLoadMore) {
      if (pagination.hasMore && !isLoadingMore && !isLoadingRef.current && productsStatus === "succeeded") {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        loadProducts(currentCategoryId, nextPage, selectedSubcategoryId);
      }
    }
  }, [currentCategoryId, currentPage, pagination, isLoadingMore, productsStatus, isSearchMode, isOffersMode, loadProducts, isNearBottom, selectedSubcategoryId]);

  // Add scroll event listener for infinite scroll with optimized throttling
  useEffect(() => {
    const throttledHandleScroll = throttle(handleScroll, 100); // Reduced from 300ms to 100ms
    window.addEventListener("scroll", throttledHandleScroll, { passive: true }); // Add passive for better performance
    return () => window.removeEventListener("scroll", throttledHandleScroll);
  }, [handleScroll]);

  // Optimized throttle function with requestAnimationFrame for better performance
  function throttle(func, wait) {
    let timeout;
    let lastCall = 0;
    return function executedFunction(...args) {
      const now = Date.now();
      if (now - lastCall >= wait) {
        lastCall = now;
        func(...args);
      } else {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          lastCall = Date.now();
          func(...args);
        }, wait - (now - lastCall));
      }
    };
  }


  // Sticky handler like plain JS
  useEffect(() => {
    const handleScroll = () => {
      if (!stickyRef.current) return;
      const stickyTop = stickyRef.current.offsetTop;
      setIsSticky(window.pageYOffset > stickyTop);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate total cart items
  const totalCartItems = cartItems
    ? cartItems.reduce((total, item) => total + (item.quantity || 0), 0)
    : 0;

  // Cart button refs and handlers
  const iconRef = useRef(null);
  const handleCartMouseEnter = () => {
    if (iconRef.current) {
      iconRef.current.style.transform = "scale(1.1) rotate(5deg)";
    }
  };
  const handleCartMouseLeave = () => {
    if (iconRef.current) {
      iconRef.current.style.transform = "scale(1) rotate(0deg)";
    }
  };

  return (
    <>
      <style>{mobileStyles}</style>
      
      {/* Show location button when no banner (regardless of products) */}
      {!hasBanner && !isSearchMode && !isOffersMode && (
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 pt-16 md:pt-32 pb-0">
            <LocationButton />
        </div>
      )}
      
      <section
        className={`${
          isSticky ? "pt-4 md:pt-6" : ""
        } ${
          !hasBanner && categories && categories.length > 0 && !isSearchMode && !isOffersMode
            ? "pt-0 md:pt-0" 
            : !hasBanner && isOffersMode && products && products.length === 0
            ? "pt-20 md:pt-28" 
            : !hasBanner && isOffersMode
            ? "pt-20 md:pt-32"
            : !hasBanner && isSearchMode && products && products.length === 0
            ? "pt-0 md:pt-0"
            : !hasBanner && isSearchMode
            ? "pt-20 md:pt-32"
            : !hasBanner 
            ? "pt-0 md:pt-0" 
            : "md:my-12"
        } max-w-screen-xl mx-auto px-4 md:px-6 pb-8 md:pb-0`}
      >
        {/* Sticky Header - Only show when not in search mode and not in offers mode */}
        {!isSearchMode && !isOffersMode && (
          <div
            ref={stickyRef}
            className={`${
              isSticky
                ? "fixed left-0 right-0 z-50 bg-white shadow-md sticky-categories"
                : ""
            }`}
          >
            <AutoScrollCategories
              categories={limitedCategories}
              categoriesStatus={categoriesStatus}
              selectedCategory={menuTab}
              onCategorySelect={handleMenuTabs}
              onFilterClick={() => setShowCategoryModal(true)}
              isSticky={isSticky}
            />
            
            {/* Subcategories Display - Show below categories if they exist */}
            {(() => {
              // Handle different data structures: list, data.list, or array directly
              // Redux stores: action.payload.data || action.payload
              // Response structure: { status: true, data: { list: [...] } }
              // So Redux will store: { list: [...] } (from action.payload.data)
              let subCategoriesList = [];
              if (subCategories) {
                if (Array.isArray(subCategories)) {
                  subCategoriesList = subCategories;
                } else if (subCategories.list && Array.isArray(subCategories.list)) {
                  // Most common: { list: [...] }
                  subCategoriesList = subCategories.list;
                } else if (subCategories.data && subCategories.data.list && Array.isArray(subCategories.data.list)) {
                  // If full response object is stored: { data: { list: [...] } }
                  subCategoriesList = subCategories.data.list;
                } else if (subCategories.data && Array.isArray(subCategories.data)) {
                  subCategoriesList = subCategories.data;
                }
              }
              
              // Filter subcategories to only show those that belong to the current category
              // This prevents showing old subcategories from previous category
              const filteredSubCategories = subCategoriesList.filter((subCat) => {
                const subCatCategoryId = subCat.ProductCategory?.id;
                return subCatCategoryId === currentCategoryId;
              });
              
              return filteredSubCategories.length > 0 ? (
                <div 
                  className={`bg-white border-b border-gray-200 px-4 md:px-6 py-3 ${subCategoriesOverflow && !subCategoriesExpanded ? 'md:pb-4' : 'md:py-4'}`}
                >
                  <div 
                    ref={subCategoriesContainerRef}
                    className="flex flex-wrap gap-2 md:gap-3"
                    style={{
                      maxHeight: subCategoriesOverflow && !subCategoriesExpanded ? `${firstRowHeight}px` : 'none',
                      overflow: subCategoriesOverflow && !subCategoriesExpanded ? 'hidden' : 'visible',
                      transition: 'max-height 0.3s ease-out'
                    }}
                  >
                    {/* "All" button to show all products in category */}
                    <button
                      onClick={() => {
                        setSelectedSubcategoryId(null);
                        dispatch(clearProducts());
                        if (currentCategoryId) {
                          loadProducts(currentCategoryId, 1, null);
                        }
                        setShowCategoryModal(false); // Close drawer when subcategory is selected
                        setSubCategoriesExpanded(false); // Collapse expanded subcategories area
                      }}
                      className="px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2"
                      style={{
                        backgroundColor: selectedSubcategoryId === null 
                          ? (theme.colors.categorySelectedTextBg || theme.colors.highlightedBg || theme.colors.primaryColor + "15")
                          : (theme.colors.highlightedBg || theme.colors.surfaceColor || "#f9fafb"),
                        color: selectedSubcategoryId === null
                          ? (theme.colors.categorySelectedText || theme.colors.primaryColor || theme.colors.textPrimary)
                          : theme.colors.textPrimary,
                        border: `1px solid ${
                          selectedSubcategoryId === null
                            ? (theme.colors.categoryImageBorder || theme.colors.primaryColor || theme.colors.borderColor)
                            : (theme.colors.borderColor || "#e5e7eb")
                        }`,
                      }}
                    >
                      <span>All</span>
                    </button>
                    
                    {filteredSubCategories.map((subCategory, index) => {
                      const isSelected = selectedSubcategoryId === subCategory.id;
                      return (
                        <button
                          key={subCategory.id || index}
                          onClick={() => {
                            // Handle subcategory selection - fetch products for this subcategory
                            if (currentCategoryId && subCategory.id) {
                              setSelectedSubcategoryId(subCategory.id);
                              dispatch(clearProducts());
                              loadProducts(currentCategoryId, 1, subCategory.id);
                              setShowCategoryModal(false); // Close drawer when subcategory is selected
                              setSubCategoriesExpanded(false); // Collapse expanded subcategories area
                            }
                          }}
                          className="px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2"
                          style={{
                            backgroundColor: isSelected 
                              ? (theme.colors.categorySelectedTextBg || theme.colors.highlightedBg || theme.colors.primaryColor + "15")
                              : (theme.colors.highlightedBg || theme.colors.surfaceColor || "#f9fafb"),
                            color: isSelected
                              ? (theme.colors.categorySelectedText || theme.colors.primaryColor || theme.colors.textPrimary)
                              : theme.colors.textPrimary,
                            border: `1px solid ${
                              isSelected
                                ? (theme.colors.categoryImageBorder || theme.colors.primaryColor || theme.colors.borderColor)
                                : (theme.colors.borderColor || "#e5e7eb")
                            }`,
                          }}
                        >
                          {/* Subcategory Image */}
                          {subCategory.DocImage?.imageUrl ? (
                            <img
                              src={subCategory.DocImage.imageUrl}
                              alt={subCategory.name || subCategory.subCategoryName}
                              className="w-5 h-5 md:w-6 md:h-6 object-contain rounded-full"
                              style={{
                                border: `1px solid ${
                                  isSelected
                                    ? (theme.colors.categoryImageBorder || theme.colors.primaryColor)
                                    : (theme.colors.borderColor || "#e5e7eb")
                                }`,
                              }}
                            />
                          ) : null}
                          <span>{subCategory.name || subCategory.subCategoryName}</span>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Expand button - Show only when subcategories overflow, centered below */}
                  {subCategoriesOverflow && (
                    <div className="flex justify-center mt-2">
                      <button
                        onClick={() => setSubCategoriesExpanded(!subCategoriesExpanded)}
                        className="bg-white border border-gray-300 rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-110 flex items-center justify-center"
                        style={{
                          borderColor: theme.colors.borderColor || "#d1d5db",
                          color: theme.colors.primaryColor || theme.colors.textPrimary,
                          transform: subCategoriesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                        title={subCategoriesExpanded ? "Show less" : "Show all subcategories"}
                      >
                        <FaChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ) : null;
            })()}
          </div>
        )}


        {/* Category filter button for offers mode with no products */}
        {isOffersMode && products && products.length === 0 && (
          <div className={`flex justify-center mb-2 ${!hasBanner ? "mt-20 md:mt-28" : "mt-4"}`}>
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Category filter button for search mode with products found */}
        {isSearchMode && products && products.length > 0 && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Category filter button for offers mode with products found */}
        {isOffersMode && products && products.length > 0 && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Products Grid */}
        <div
          ref={productsGridRef}
          className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 lg:gap-8 ${
            !isSearchMode ? "mt-4 md:mt-8" : isSearchMode && products.length === 0 && !hasBanner ? "pt-0" : "pt-4"
          } h-[420px] transition-opacity duration-300 ${
            isTransitioning ? "opacity-50" : "opacity-100"
          } md:mb-12`}
        >
          {productsStatus === "loading" && productsArray.length === 0 ? (
            Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="animate-pulse"
                style={{
                  animationDelay: `${index * 0.1}s`,
                  animationFillMode: 'both'
                }}
              >
                <Skeleton />
              </div>
            ))
          ) : products.length === 0 ? (
            <div className="col-span-4 flex items-center justify-center py-4 h-[420px]">
              <div className="bg-white/70 backdrop-blur-md rounded-3xl shadow-2xl px-8 py-10 flex flex-col items-center max-w-md w-full border border-gray-100">
                  <svg
                    className="w-20 h-20 mb-6 text-primary/80"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      cx="11"
                      cy="11"
                      r="8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="#f3f4f6"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-4.35-4.35"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M9.5 10.5c.5-1 2.5-1 3 0"
                      stroke="#f91944"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <circle cx="9" cy="9" r="1" fill="#f91944" />
                    <circle cx="13" cy="9" r="1" fill="#f91944" />
                  </svg>
                  <h2 className="text-2xl font-extrabold text-primary mb-2 poppins text-center">
                    {isOffersMode ? "No offers found" : "No items found"}
                  </h2>
                  <p className="text-gray-500 text-base text-center mt-2">
                    {isSearchMode 
                      ? "We couldn't find what you're looking for." 
                      : isOffersMode
                      ? "We couldn't find any offers at the moment."
                      : "We couldn't find any menu items in this category."}
                  </p>
                  
                  {/* WhatsApp Contact - Show in both search mode and empty categories */}
                  {profile?.whatsapp && (
                    <div className="w-full mt-6 space-y-3">
                      <p className="text-gray-700 text-sm text-center font-medium">
                        {isSearchMode ? "Can't find it? Contact us on WhatsApp!" : "Looking for something? Contact us on WhatsApp!"}
                      </p>
                      <textarea
                        value={whatsappMessage}
                        onChange={(e) => setWhatsappMessage(e.target.value)}
                        placeholder={isSearchMode ? "Enter what you're looking for..." : isOffersMode ? "Hi, I'm looking for: offer" : "Tell us what you need..."}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl resize-none focus:outline-none focus:border-primary transition-colors text-sm"
                        rows="3"
                      />
                      <button
                        onClick={() => {
                          if (!profile?.whatsapp) {
                            return;
                          }
                          
                          // Use user's message or default message
                          const userMessage = whatsappMessage.trim();
                          let message;
                          
                          if (userMessage) {
                            // Construct professional message with user input
                            message = isSearchMode ? "🔍 *PRODUCT INQUIRY*\n\n" : "📦 *CATEGORY INQUIRY*\n\n";
                            message += "📋 *INQUIRY DETAILS*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += `${isSearchMode ? 'Looking for' : 'Message'}: *${userMessage}*\n`;
                            message += isSearchMode ? "Status: Item not found in online menu\n\n" : "Status: Category has no items\n\n";
                            
                            message += "❓ *QUESTIONS*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += "• Is this item available?\n";
                            message += "• What is the price?\n";
                            message += "• Can I place an order?\n\n";
                            
                            message += "🏪 *STORE INFORMATION*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += `Store Name: ${profile.name || 'N/A'}\n`;
                            if (profile.location) {
                              message += `Location: ${profile.location}\n`;
                            }
                            if (profile.mobile) {
                              message += `Phone: ${profile.mobile}\n`;
                            }
                            if (profile.link) {
                              message += `Website: ${profile.link}\n`;
                            }
                            message += "\n";
                            
                            message += "⏰ *INQUIRY TIME*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += `Date: ${new Date().toLocaleDateString()}\n`;
                            message += `Time: ${new Date().toLocaleTimeString()}\n\n`;
                            
                            message += "Thank you for your assistance! 🙏";
                          } else {
                            // Default message when no user input
                            message = `Hello! I would like to place an order at ${profile.name || 'your store'}.`;
                          }
                          
                          const url = `https://wa.me/${profile.whatsapp}?text=${encodeURIComponent(message)}`;
                          window.open(url, '_blank');
                        }}
                        className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <FaWhatsapp className="w-5 h-5" />
                        Connect on WhatsApp
                      </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            (() => {
              // Process products to swap highlighted items in even positions
              const processedProducts = [...productsArray];

              for (let i = 0; i < processedProducts.length; i++) {
                const currentItem = processedProducts[i];
                const position = i + 1; // index + 1 for position

                // Check if current item is highlighted and in even position
                if (currentItem?.highlight && position % 2 === 0 && i > 0) {
                  const previousItem = processedProducts[i - 1];

                  // Check if previous item is not highlighted
                  if (!previousItem?.highlight) {
                    // Swap positions
                    processedProducts[i] = previousItem;
                    processedProducts[i - 1] = currentItem;
                  }
                }
              }

              return processedProducts.map((item, index) => {
                const currentCategory = categoriesArray?.find(
                  (cat) => cat.name === menuTab
                );
                return (
                  <div
                    key={item.id}
                    className="animate-fade-in-up"
                    style={{
                      animationDelay: `${Math.min(index * 0.05, 0.5)}s`, // Reduced delay for faster appearance
                      animationFillMode: 'both'
                    }}
                  >
                    <FoodItem
                    {...item}
                    currency={profile?.currency || "AED"}
                    image={item?.DocImage?.imageUrl}
                    is_veg={item?.is_veg}
                    categoryName={currentCategory?.name}
                    categoryDescription={currentCategory?.description}
                    showAddToCart={showAddToCart}
                      highlightInfoIcon={highlightInfoIcon}
                    highlight={item?.highlight || false}
                  />
                  </div>
                );
              });
            })()
          )}
        </div>

        {/* Loading More Indicator */}
        {isLoadingMore && (
          <div className="flex justify-center items-center py-8 animate-fade-in">
            <div className="flex flex-col items-center space-y-3">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-t-2" style={{ borderColor: theme.colors.primaryColor }}></div>
              <p className="text-sm font-medium animate-pulse" style={{ color: theme.colors.textSecondary }}>Loading more products...</p>
            </div>
          </div>
        )}

        {/* Scroll-based Loading Hint */}
        {!isLoadingMore && productsArray.length > 0 && pagination.hasMore && productsStatus === "succeeded" && !isSearchMode && !isOffersMode && isNearBottom && (
          <div className="flex justify-center items-center py-4 animate-fade-in">
            <div className="flex items-center space-x-2 text-sm" style={{ color: theme.colors.textSecondary }}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: theme.colors.primaryColor }}></div>
              <span>Scroll down for more products...</span>
            </div>
          </div>
        )}

        {/* Manual Load More Button - for debugging and fallback */}
        {!isLoadingMore && productsArray.length > 0 && pagination.hasMore && productsStatus === "succeeded" && !isSearchMode && !isOffersMode && !isNearBottom && (
          <div className="flex justify-center items-center py-8 animate-fade-in-up">
            <button
              onClick={() => {
                const nextPage = currentPage + 1;
                setCurrentPage(nextPage);
                loadProducts(currentCategoryId, nextPage, selectedSubcategoryId);
              }}
              className="px-6 py-3 rounded-full font-semibold shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl animate-bounce-subtle"
              style={{
                backgroundColor: theme.colors.primaryColor,
                color: '#ffffff',
              }}
            >
              Load More Products ({productsArray.length} / {pagination.totalItems})
            </button>
          </div>
        )}

        {/* End of Products Message */}
        {!isLoadingMore && productsArray.length > 10 && !pagination.hasMore && productsStatus === "succeeded" && !isSearchMode && !isOffersMode && (
          <div className="flex justify-center items-center py-8 animate-fade-in-up">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl px-8 py-4 shadow-lg border border-blue-100">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">All caught up!</p>
                  <p className="text-xs text-gray-500">You've seen all {pagination.totalItems} products</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cart Button with Count - Hidden on Mobile */}
        {showCartButton && (
          <div className="fixed z-50 bottom-6 right-6 hidden md:block">
            <button
              className="rounded-full shadow-2xl w-20 h-20 flex items-center justify-center text-4xl border-2 border-opacity-90 transition-transform duration-300 hover:scale-110 relative"
              style={{
                background: `linear-gradient(to bottom right, ${theme.colors.cartIconGradientFrom}, ${theme.colors.cartIconGradientVia}, ${theme.colors.cartIconGradientTo})`,
                color: theme.colors.cartIconText,
                borderColor: theme.colors.cartIconBorder,
                borderWidth: "2.5px",
                boxShadow: theme.colors.cartIconShadow,
              }}
              aria-label="View Cart"
              onClick={() => navigate(`/${profile?.key}/orders`)}
              onMouseEnter={handleCartMouseEnter}
              onMouseLeave={handleCartMouseLeave}
            >
              <span ref={iconRef} className="inline-block text-5xl font-bold">
                <BsCart2 />
              </span>
              {totalCartItems > 0 && (
                <div
                  className="text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center z-10 border-2 shadow-lg absolute -top-1 -right-1"
                  style={{
                    backgroundColor: theme.colors.cartIconBadgeBg,
                    color: theme.colors.cartIconBadgeText,
                    borderColor: theme.colors.cartIconBorder,
                  }}
                >
                  {totalCartItems}
                </div>
              )}
            </button>
          </div>
        )}

        {/* Category Selection Modal */}
        {showCategoryModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-4 pb-32 md:pb-12"
            onClick={() => setShowCategoryModal(false)}
          >
            <div
              className="bg-white rounded-t-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
              style={{ backgroundColor: theme.colors.backgroundColor }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                className="flex items-center justify-between p-4 border-b"
                style={{ borderColor: theme.colors.borderColor }}
              >
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                  style={{ color: theme.colors.textSecondary }}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
                <h3
                  className="text-lg font-semibold"
                  style={{ color: theme.colors.textPrimary }}
                >
                  Menu categories
                </h3>
                <div className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                  {categoriesArray.length}/{categoriesPagination.totalItems || '?'}
                </div>
              </div>

              {/* Search Input */}
              <div className="p-4 border-b" style={{ borderColor: theme.colors.borderColor }}>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: theme.colors.textSecondary }} />
                  <input
                    type="text"
                    value={categorySearchQuery}
                    onChange={(e) => setCategorySearchQuery(e.target.value)}
                    placeholder="Search categories..."
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      borderColor: theme.colors.borderColor,
                      backgroundColor: theme.colors.backgroundColor,
                      color: theme.colors.textPrimary,
                    }}
                  />
                </div>
              </div>

              {/* Modal Content - Vertical List with Infinite Scroll */}
              <div
                className="overflow-y-auto flex-1"
                style={{
                  maxHeight: "60vh",
                  minHeight: "200px",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                  scrollbarColor: `${theme.colors.borderColor} transparent`,
                }}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                  const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
                  // Load more when scrolled to 80%
                  if (scrollPercentage > 0.8 && !isLoadingMoreCategories && categoriesPagination.hasMore) {
                    loadMoreCategories();
                  }
                }}
              >
                {categoriesArray &&
                  categoriesArray.map((categoryItem, index) => (
                    <button
                      key={categoryItem.id || index}
                      onClick={() => {
                        handleMenuTabs(categoryItem.name, index);
                        setShowCategoryModal(false);
                      }}
                      className="w-full flex items-center justify-between py-4 px-4 hover:bg-gray-50 transition-colors border-l-4 min-h-[60px]"
                      style={{
                        borderLeftColor:
                          menuTab === categoryItem.name
                            ? theme.colors.primaryColor
                            : "transparent",
                        backgroundColor:
                          menuTab === categoryItem.name
                            ? theme.colors.primaryColor + "05"
                            : "transparent",
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        {categoryItem.DocImage?.imageUrl ? (
                          <img
                            src={categoryItem.DocImage.imageUrl}
                            alt={categoryItem.name}
                            className="w-6 h-6 object-contain rounded-full"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full flex items-center justify-center">
                            <span className="text-lg">🍽️</span>
                          </div>
                        )}
                        <span
                          className="font-medium"
                          style={{
                            color: theme.colors.textPrimary,
                          }}
                        >
                          {categoryItem.name}
                        </span>
                      </div>
                    </button>
                  ))}

                {/* Category Loading More Indicator */}
                {isLoadingMoreCategories && (
                  <div className="flex justify-center items-center py-4">
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: theme.colors.primaryColor }}></div>
                      <span className="text-sm" style={{ color: theme.colors.textSecondary }}>Loading more categories...</span>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}

      </section>
    </>
  );
};

export default Foods;
